import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import React, { useState } from "react";
import { Drawer } from "vaul";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { FDProduct } from "@/app/interface/fd";
import { formatIndianCurrency } from "@/app/utils/helper";
import Info from "@/app/assets/icons/info";
import { X } from "lucide-react";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
} from "@/components/ui/select";

interface ScrollableProps {
    tenure: number;
    amtInvested: number;
    isEditing: boolean;
    isSeniorCitizen: boolean;
    isWomanBenefit: boolean;
    setamtInvested: (value: number) => void;
    setIsEditing: (value: boolean) => void;
    setIsModalOpen: (value: boolean) => void;
    setTenure: (value: number) => void;
    fdData: FDProduct;
    getInterest: () => number;
    getMaturityAmount: () => number;
    setisSeniorCitizen: (value: boolean) => void;
    setisWomanBenefit: (value: boolean) => void;
}

const ScrollableBtn: React.FC<ScrollableProps> = ({
    amtInvested,
    setamtInvested,
    isSeniorCitizen,
    isWomanBenefit,
    tenure,
    isEditing,
    setIsEditing,
    setIsModalOpen,
    setTenure,
    fdData,
    getInterest,
    getMaturityAmount,
    setisSeniorCitizen,
    setisWomanBenefit,
}) => {
    const [isWidgetOpen, setisWidgetOpen] = useState<boolean>(false);
    const [isDropdownOpen, setisDropdownOpen] = useState(false);

    const popularAmount =
        fdData?.cardAmounts && fdData?.cardAmounts?.every((amt) => amt?.value > 0)
            ? fdData?.cardAmounts?.find((box) => box.tagStatus)?.value ||
              fdData?.cardAmounts[0]?.value
            : fdData?.minAmt;

    const onWidgetClose = () => {
        setisDropdownOpen(false);
        setisWidgetOpen(false);
        setamtInvested(popularAmount);
    };

    return (
        <>
            {isWidgetOpen ? (
                <Drawer.Root open={isWidgetOpen} onClose={onWidgetClose} dismissible={false}>
                    <Drawer.Portal>
                        <Drawer.Title>
                            <VisuallyHidden>FD Widget</VisuallyHidden>
                        </Drawer.Title>
                        <Drawer.Overlay className="fixed inset-0 bg-black/80" />
                        <Drawer.Content
                            onClick={(e) => e.stopPropagation()}
                            className="fixed bottom-0 left-0 right-0 mb-7 flex h-auto flex-col rounded-t-[10px] bg-white outline-none"
                        >
                            <div
                                className="px-s9"
                                onClick={() => {
                                    if (isDropdownOpen) {
                                        setisDropdownOpen(false);
                                    }
                                }}
                            >
                                <div className="flex items-center justify-between">
                                    <h3 className="py-s9 text-xl font-semibold leading-[130%]">
                                        Calculate your earnings
                                    </h3>
                                    <X onClick={onWidgetClose} />
                                </div>
                                <div
                                    className={`${
                                        !fdData?.cardAmounts ||
                                        fdData?.cardAmounts?.every((amt) => amt?.value === 0)
                                            ? "pt-s7"
                                            : "py-s7"
                                    } border-t-2 border-dashed border-neutral-50`}
                                >
                                    <div className="flex h-8 justify-between">
                                        <p className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                            Invested amount
                                        </p>
                                        <input
                                            type={!isEditing ? "text" : "number"}
                                            max={fdData?.maxAmt}
                                            id="amount-input"
                                            value={
                                                isEditing
                                                    ? amtInvested || ""
                                                    : amtInvested
                                                      ? formatIndianCurrency(amtInvested)
                                                      : ""
                                            }
                                            onFocus={() => setIsEditing(true)}
                                            onBlur={() => {
                                                setIsEditing(false);
                                                if (
                                                    amtInvested === null ||
                                                    amtInvested === undefined
                                                ) {
                                                    setamtInvested(0);
                                                }
                                            }}
                                            onChange={(e) => {
                                                const value =
                                                    e.target.value === ""
                                                        ? 0
                                                        : Math.min(
                                                              Number(e.target.value),
                                                              fdData?.maxAmt || 0,
                                                          );
                                                setamtInvested(value >= 0 ? value : 0);
                                            }}
                                            onKeyDown={(e) => {
                                                if (
                                                    e.key === "-" ||
                                                    e.key === "e" ||
                                                    e.key === "E" ||
                                                    e.key === "+"
                                                ) {
                                                    e.preventDefault();
                                                }
                                            }}
                                            className="w-28 border-none bg-transparent text-right text-md font-medium leading-[160%] tracking-[0.14px] focus:outline-none"
                                            placeholder="0"
                                        />
                                    </div>
                                </div>
                                {fdData?.cardAmounts &&
                                fdData?.cardAmounts?.every((amt) => amt?.value > 0) ? (
                                    <div className="flex justify-center gap-3">
                                        {fdData?.cardAmounts
                                            ?.sort((a, b) => a.value - b.value)
                                            ?.map((box, index) => (
                                                <div
                                                    key={index}
                                                    onClick={() => setamtInvested(box.value)}
                                                    className="relative flex flex-col items-center"
                                                >
                                                    {box.tagStatus && (
                                                        <span className="absolute -top-5 rounded-md bg-red-100 px-2 py-1 text-xs font-bold text-black">
                                                            {box?.tag}
                                                        </span>
                                                    )}

                                                    <div
                                                        className={`cursor-pointer rounded-lg border-2 border-neutral-100 px-3 py-[6px] text-neutral-500 ${Number(amtInvested) === Number(box.value) ? "bg-slate-50" : "bg-white"} hover:bg-slate-50`}
                                                    >
                                                        {formatIndianCurrency(box.value)}
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                ) : null}

                                <div
                                    className={`${
                                        !fdData?.cardAmounts ||
                                        fdData?.cardAmounts?.every((amt) => amt?.value === 0)
                                            ? "pt-s5"
                                            : "pt-s7"
                                    }`}
                                >
                                    <div className="flex h-8 justify-between">
                                        <p className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                            Interest rate (p.a.)
                                        </p>
                                        <p className="text-md font-medium leading-[160%] tracking-[0.14px]">
                                            {parseFloat(String(getInterest())).toFixed(2)}%
                                        </p>
                                    </div>
                                </div>

                                <div className="mt-s4 flex h-[19px] items-center justify-between">
                                    <div className="flex items-center gap-[6px]">
                                        <input
                                            type="checkbox"
                                            id="senior-citizen"
                                            checked={isSeniorCitizen}
                                            className="h-4 w-4 cursor-pointer rounded-lg accent-brandAccent"
                                            onChange={(e) => setisSeniorCitizen(e.target.checked)}
                                        />

                                        <div className="flex items-center gap-1">
                                            <label
                                                htmlFor="senior-citizen"
                                                className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500"
                                            >
                                                Senior citizen
                                            </label>
                                            <div className="group relative">
                                                <Info
                                                    width="13"
                                                    height="13"
                                                    className="text-neutral-300"
                                                />
                                                <div className="absolute bottom-full left-[-5rem] mb-2 hidden w-36 rounded-md bg-black p-2 text-center text-xs text-white group-hover:block">
                                                    Age 60 and above
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-[6px]">
                                        <input
                                            type="checkbox"
                                            id="woman-benefit"
                                            checked={isWomanBenefit}
                                            className="h-4 w-4 cursor-pointer accent-brandAccent"
                                            onChange={(e) => setisWomanBenefit(e.target.checked)}
                                        />
                                        <label
                                            htmlFor="woman-benefit"
                                            className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500"
                                        >
                                            Woman Benefit
                                        </label>
                                    </div>
                                </div>

                                <div className="mb-3 mt-4 flex h-8 items-center justify-between">
                                    <div className="flex items-center text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                        Tenure in months &nbsp;
                                        <Select
                                            open={isDropdownOpen}
                                            onOpenChange={(open) => {
                                                setisDropdownOpen(open);
                                            }}
                                            onValueChange={(value) => {
                                                setTenure(Number(value));
                                                setisDropdownOpen(false);
                                            }}
                                        >
                                            <SelectTrigger className="w-auto appearance-none border-none bg-transparent p-0 focus:outline-none focus:ring-0 focus:ring-offset-0"></SelectTrigger>
                                            <SelectContent className="w-[40px]">
                                                <SelectGroup>
                                                    {fdData?.returnsAndTenure.map((item, index) => {
                                                        return (
                                                            <SelectItem
                                                                key={index}
                                                                value={String(item?.tenure)}
                                                            >
                                                                {item.tenure} M
                                                            </SelectItem>
                                                        );
                                                    })}
                                                </SelectGroup>
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    <p className="text-md font-medium leading-[160%] tracking-[0.14px]">
                                        {tenure} Months
                                    </p>
                                </div>

                                <div className="border-t-2 border-dashed border-neutral-50 py-s9">
                                    <div className="flex h-8 justify-between">
                                        <p className="text-sm leading-[160%] tracking-[0.14px] text-neutral-500">
                                            Maturity amount
                                        </p>
                                        <p className="text-lg font-semibold leading-[164%] tracking-[-0.18px]">
                                            {formatIndianCurrency(getMaturityAmount())}
                                        </p>
                                    </div>

                                    {(amtInvested ?? 0) < fdData?.minAmt && (
                                        <p className="text-center text-sm text-red-500">
                                            Minimum invesment should be{" "}
                                            {formatIndianCurrency(fdData?.minAmt)}
                                        </p>
                                    )}

                                    <RegularButton
                                        noIcon={true}
                                        label="Book Now"
                                        showRightIcon={false}
                                        disabled={
                                            (amtInvested ?? 0) >= fdData?.minAmt ? false : true
                                        }
                                        variant={ButtonVariant.PrimaryDefault}
                                        size={ButtonSize.Medium}
                                        className="mt-s6 min-w-full max-w-full"
                                        onClick={() => setIsModalOpen(true)}
                                    />
                                </div>
                            </div>
                        </Drawer.Content>
                    </Drawer.Portal>
                </Drawer.Root>
            ) : (
                <div className="fixed bottom-0 left-0 z-10 block w-full md:hidden">
                    <div className="w-full bg-white px-4 py-3 shadow-md">
                        <div className="container mx-auto flex items-center justify-center">
                            <RegularButton
                                noIcon={true}
                                label="Book Now"
                                showRightIcon={false}
                                variant={ButtonVariant.PrimaryDefault}
                                size={ButtonSize.Medium}
                                className="min-w-full max-w-full flex-1"
                                onClick={() => {
                                    setamtInvested(popularAmount);
                                    setisDropdownOpen(false);
                                    setisWidgetOpen(true);
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ScrollableBtn;
